import { useState } from "react";
import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
import { saveAs } from "file-saver";

import UserInfo from "src/components/common/userInfo";
import { getFileNameFromUrl, testerEmails } from "src/components/common/functions";
import { useAuthStore, User } from "../main/authStore";

import { Proposal } from "./types";

export default function ProposalDetail() {
    const { proposal } = useRouteLoaderData("Proposal") as { proposal: Proposal };
    const user = useAuthStore(state => state.user) as User;
    const submit = useSubmit();
    const [comments, setComments] = useState(proposal.approverNotes ?? "");
    const isFinal = ["Approved", "Rejected"].includes(proposal.status);
    const canEdit = user.projects !== "no" && proposal.void === "no" && !isFinal;
    const canApprove = proposal.approverId === user.id && proposal.void === "no" && !isFinal;

    const submitSimple = (action: string, values: Record<string, string>) => {
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => formData.append(key, value));
        submit(formData, { method: "post", action });
    };

    const handleDownload = async (url: string) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(url) ?? "proposal.pdf");
        } catch (error) {
            window.alert("Error downloading file:" + error);
        }
    };

    return (
        <div className = "border" style = {{ borderRadius: "6px" }}>
            <div className = "cssSeperateWhenWide p-3">
                <table className = "table table-responsive-xl noTopBorder flex-grow-1 me-3">
                    <tbody>
                        <tr>
                            <th>Status</th>
                            <td><span className = "badge text-bg-primary">{proposal.status}</span></td>
                        </tr>
                        <tr>
                            <th>Proposal Number</th>
                            <td>{proposal.proposalNumber}</td>
                        </tr>
                        <tr>
                            <th>Proposal Date</th>
                            <td>{proposal.proposalDate}</td>
                        </tr>
                        <tr>
                            <th>Requester</th>
                            <td><UserInfo name = {proposal.requesterName} id = {proposal.requesterId} /></td>
                        </tr>
                        <tr>
                            <th>Department</th>
                            <td>{proposal.department}</td>
                        </tr>
                        {proposal.projectId ? (
                            <tr>
                                <th>Project</th>
                                <td><Link to = {`/Projects/${proposal.projectId}`}>{proposal.projectName}</Link></td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Approver</th>
                            <td><UserInfo name = {proposal.approverName} id = {proposal.approverId} /></td>
                        </tr>
                        <tr>
                            <th>Notes</th>
                            <td><span style = {{ whiteSpace: "pre-wrap" }}>{proposal.notes}</span></td>
                        </tr>
                    </tbody>
                </table>
                <table className = "table table-responsive-xl noTopBorder flex-grow-1">
                    <tbody>
                        <tr>
                            <th>Subtotal</th>
                            <td>${proposal.subtotal}</td>
                        </tr>
                        <tr>
                            <th>Tax</th>
                            <td>${proposal.tax}</td>
                        </tr>
                        <tr>
                            <th>Discount</th>
                            <td>${proposal.discount}</td>
                        </tr>
                        <tr>
                            <th>Total</th>
                            <td>${proposal.total}</td>
                        </tr>
                        <tr>
                            <th>Submitted By</th>
                            <td>
                                <UserInfo name = {proposal.submitterName} id = {proposal.submitterId} />
                                <span className = "ms-2">{proposal.submitTime}</span>
                            </td>
                        </tr>
                        <tr>
                            <th>Approval Time</th>
                            <td>{proposal.approvalTime}</td>
                        </tr>
                        <tr>
                            <th>Approver Notes</th>
                            <td><span style = {{ whiteSpace: "pre-wrap" }}>{proposal.approverNotes}</span></td>
                        </tr>
                        {proposal.notifiedAt ? (
                            <tr>
                                <th>Notified By</th>
                                <td>
                                    <UserInfo name = {proposal.notifierName} id = {proposal.notifiedBy} />
                                    <span className = "ms-2">{proposal.notifiedAt}</span>
                                </td>
                            </tr>
                        ) : null}
                        <tr>
                            <th>Creator</th>
                            <td>
                                <UserInfo name = {proposal.creatorName} id = {proposal.creatorId} />
                                <span className = "ms-2">{proposal.createdAt}</span>
                            </td>
                        </tr>
                        {proposal.updaterId ? (
                            <tr>
                                <th>Updater</th>
                                <td>
                                    <UserInfo name = {proposal.updaterName} id = {proposal.updaterId} />
                                    <span className = "ms-2">{proposal.updatedAt}</span>
                                </td>
                            </tr>
                        ) : null}
                        {proposal.void === "yes" ? (
                            <>
                                <tr>
                                    <th className = "text-danger">Void Reason</th>
                                    <td>{proposal.voidReason}</td>
                                </tr>
                                <tr>
                                    <th>Validate Reason</th>
                                    <td>{proposal.validateReason}</td>
                                </tr>
                            </>
                        ) : null}
                    </tbody>
                </table>
            </div>
            <div className = "table-responsive px-3">
                <table className = "table">
                    <thead className = "noTableTopBorder">
                        <tr>
                            <th>Service</th>
                            <th>Description</th>
                            <th>Unit Price</th>
                            <th>Quantity</th>
                            <th>Line Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {proposal.data.map((line, index) => (
                            <tr key = {index}>
                                <td>{line.serviceId ? <Link to = {`/Services/${line.serviceId}`}>{line.serviceName}</Link> : line.serviceName}</td>
                                <td>{line.description}</td>
                                <td>${line.unitPrice}</td>
                                <td>{line.quantity}</td>
                                <td>${line.lineTotal}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className = "mb-3 px-3">
                {canEdit ? <Link className = "btn btn-outline-primary" to = "Edit">Edit</Link> : null}
                {proposal.void === "no" ? (
                    <button
                        type = "button"
                        className = "btn btn-danger ms-2"
                        onClick = {() => {
                            if (!window.confirm("Are you sure to void this proposal?")) return;
                            let voidReason = window.prompt("reason to void:");
                            if (voidReason === null) return;
                            while (voidReason === "") {
                                window.alert("Reason is required to void.");
                                voidReason = window.prompt("reason to void:");
                                if (voidReason === null) return;
                            }
                            submitSimple("Void", { id: String(proposal.id), voidReason });
                        }}
                    >
                        Void
                    </button>
                ) : (
                    <>
                        {testerEmails.includes(user.email) ? (
                            <button
                                type = "button"
                                className = "btn btn-primary ms-2"
                                onClick = {() => {
                                    if (!window.confirm("Are you sure to validate this proposal?")) return;
                                    let validateReason = window.prompt("reason to validate:");
                                    if (validateReason === null) return;
                                    while (validateReason === "") {
                                        window.alert("Reason is required to validate.");
                                        validateReason = window.prompt("reason to validate:");
                                        if (validateReason === null) return;
                                    }
                                    submitSimple("Validate", { id: String(proposal.id), validateReason });
                                }}
                            >
                                Validate
                            </button>
                        ) : null}
                        <button
                            type = "button"
                            className = "btn btn-danger ms-2"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to delete this proposal?\n\nThis action cannot be undone.")) return;
                                submitSimple("Delete", { id: String(proposal.id) });
                            }}
                        >
                            Delete
                        </button>
                    </>
                )}
                {proposal.void === "no" && !isFinal && proposal.approverId !== user.id ? (
                    <button
                        type = "button"
                        className = "btn btn-info ms-2"
                        onClick = {() => {
                            if (!window.confirm("Are you sure to notify the approver?")) return;
                            submitSimple("Notify", { id: String(proposal.id) });
                        }}
                    >
                        Notify
                    </button>
                ) : null}
                {proposal.pdfId ? (
                    <button type = "button" className = "btn btn-primary ms-2" onClick = {() => handleDownload(proposal.pdfId)}>
                        PDF
                    </button>
                ) : null}
                <Link className = "btn btn-outline-secondary float-end" to = ".." relative = "path">Back</Link>
            </div>
            {canApprove ? (
                <div className = "px-3 pb-3">
                    <label className = "fw-bold mb-0" htmlFor = "approverNotes">Approver Comments:</label>
                    <textarea
                        id = "approverNotes"
                        className = "form-control"
                        rows = {4}
                        value = {comments}
                        onChange = {e => setComments(e.currentTarget.value)}
                    />
                    <div className = "mt-3">
                        <button
                            type = "button"
                            className = "btn btn-success"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to approve this proposal?")) return;
                                submitSimple("Approve", {
                                    id: String(proposal.id),
                                    approverDecision: "Approved",
                                    approverNotes: comments,
                                });
                            }}
                        >
                            Approve
                        </button>
                        <button
                            type = "button"
                            className = "btn btn-danger ms-2"
                            onClick = {() => {
                                if (!window.confirm("Are you sure to reject this proposal?")) return;
                                submitSimple("Approve", {
                                    id: String(proposal.id),
                                    approverDecision: "Rejected",
                                    approverNotes: comments,
                                });
                            }}
                        >
                            Reject
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
